import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { anchorMissing } from './tasks-help.shared';

/**
 * Kontextuelle Hilfe fuer `/settings/tasks` (Aufgaben-Einstellungen, 08.10.2026).
 * Start ueber den ?-Knopf im Settings-Kopf (`settings-dashboard.component.ts` → `helpTourId`).
 *
 * Die Seite ist als Daten beschrieben (`task-settings.sections.ts`); die Abschnitts-Anker
 * entstehen dort als `'task-settings-' + section.id`. Gescrollt wird vom Coordinator
 * (naechster Scroll-Vorfahr `.settings-content-area`), Akkordeons gibt es keine.
 *
 * Kernaussagen — aus Sicht der Leitung, die einstellt, UND der Mitarbeitenden, die es merken:
 *  - **Aus heisst unsichtbar.** Ein ausgeschalteter Schalter blendet Feld, Knopf, Spalte und
 *    Menuepunkt ganz aus, vorhandene Eintraege bleiben lesbar.
 *  - **Gesperrt heisst: erst etwas anderes einschalten.** Unter der Zeile steht, was.
 *  - **Diese Seite schraenkt nur ein.** Wer was darf, steht im Berechtigungsdialog je Person.
 */
const page = {
  placement: 'left' as const,
  nextCondition: 'manual' as const,
  showNextButton: true,
};

const section = (id: string, anchor: AnchorId, key: string, optional = false) => ({
  id,
  anchor,
  spotlightAnchor: anchor,
  title: `TUTORIAL.SETTINGS_TASKS.${key}.TITLE`,
  content: `TUTORIAL.SETTINGS_TASKS.${key}.CONTENT`,
  ...page,
  ...(optional ? { optional: true, skipIf: () => anchorMissing(anchor) } : {}),
});

export const SETTINGS_TASKS_HELP_TOUR: TourConfig = {
  id: TourFeature.SettingsTasksHelp,
  version: 1,
  title: 'SETTINGS_TASKS_HELP_TOUR_TITLE',
  description: 'SETTINGS_TASKS_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  triggerContext: { route: '/settings/tasks' },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 200,
  category: 'feature-discovery',
  renumberVisibleSteps: true,
  chapters: [
    {
      id: 'overview',
      title: 'SETTINGS_TASKS_HELP_TOUR_TITLE',
      steps: [
        { ...section('st-welcome', AnchorId.TaskSettingsModule, 'WELCOME'), showPrevButton: false },
        section('st-projects', AnchorId.TaskSettingsRowProjects, 'PROJECTS', true),
        // -v2 (11.10.2026): drei statt zwei eingerückte Schalter — „Automatisch abschließen“ (R1.4).
        { ...section('st-projects-more', AnchorId.TaskSettingsProjects, 'PROJECTS_MORE', true), audioStepId: 'st-projects-more-v2' },
        section('st-extensions', AnchorId.TaskSettingsExtensions, 'EXTENSIONS', true),
        // -v2 (10.10.2026): „und am Terminal“ entfällt — den Terminal-Schalter gibt es nicht mehr (O2).
        { ...section('st-employees', AnchorId.TaskSettingsEmployees, 'EMPLOYEES', true), audioStepId: 'st-employees-v2' },
        section('st-features', AnchorId.TaskSettingsFeatures, 'FEATURES', true),
        section('st-defaults', AnchorId.TaskSettingsDefaults, 'DEFAULTS', true),
        section('st-reminders', AnchorId.TaskSettingsReminders, 'REMINDERS', true),
        section('st-notifications', AnchorId.TaskSettingsNotifications, 'NOTIFICATIONS', true),
        section('st-permissions', AnchorId.TaskSettingsPermissions, 'PERMISSIONS', true),
      ],
    },
  ],
};
